<template>
	<div class="p-p-2 child-content">
		<DocumentClassExportStates
			:exportStates="exportStates"
			:loading="loading"
			:paginationInfo="pagination"
			@onPage="onPage($event)"
			@onFilter="onFilter"
			@onSort="onSort"
			:allExportInterfaces="allExportInterfaces"
		/>
	</div>
</template>

<script lang="ts">
import {defineComponent, onMounted, reactive, ref} from "vue";
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import DocumentClassExportStates from "@/apps/administration/components/documentclasses/DocumentClassExportStates.vue";
import {ClientManager} from "@/singletons/ClientManager";
import {ToastManager} from "@/util/ToastManager";
import {ExportInterfaceDto, PaginationDto} from "@dex/squeeze-client-ts";
import {ExportStatus} from "@dex/squeeze-client-ts/dist/api";
import {DocumentFilterObject} from "@/apps/squeeze/interfaces/DocumentSearch";
import {TableSettings} from "@/util/TableSettings";
import {useRoute} from "vue-router";
import useSqueezeStore from "@/apps/squeeze/store";

export default defineComponent({
	name: 'DocumentClassExportStatesSubView',
	components: {DocumentClassExportStates},
	props: {
		/** Currently active document-class */
		documentClassId: {
			type: Number,
			default: 0,
		},
		/** Currently active export interface */
		exportId: {
			type: Number,
			default: 0,
		},
	},
	setup(props) {
		const {t} = useI18n();
		const toast = useToast();
		const route = useRoute();

		/** Vuex Store */
		const store = useSqueezeStore();

		/** User API endpoint */
		const userApi = ClientManager.getInstance().squeeze.user;

		/** DocumentClass API endpoint */
		const documentClassService = ClientManager.getInstance().squeeze.documentClass;

		/** Show loading? **/
		const loading = ref(false);

		/** Object with all export states **/
		const exportStates = ref<ExportStatus[]>([]);

		/** Empty pagination object & Info / Options of pagination  */
		const pagination = reactive<PaginationDto>({pageSize: 25, page: 0, total: 0});

		/** Array with all export interfaces */
		const allExportInterfaces = ref<ExportInterfaceDto[]>([]);

		/** Active filter values */
		const filterValues = reactive<{
			status: string;
			id: number | undefined;
			documentId: number | undefined;
			interfaceId: number | undefined;
			result: string;
			modifiedTs: Date | undefined;
		}>({
			status: '',
			id: undefined,
			documentId: undefined,
			interfaceId: undefined,
			result: '',
			modifiedTs: undefined,
		});

		/** Active sort */
		const tableSort = reactive<{ fieldName: string; sort: string }>({
			fieldName: '',
			sort: 'ASC',
		});

		/** Gets the Document-Classes Export Interface */
		const getExportInterfaces = () => {
			loading.value = true;

			documentClassService.getAllDocumentClassExportInterfaces(props.documentClassId)
				.then(data => {
					allExportInterfaces.value = data;
				}).catch(response => response.json().then ((err: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
				})).finally(() => {
					loading.value = false;
				})
		}

		/** Get all export interface states **/
		const getExportInterfaceStates = () => {
			loading.value = true;

			const modifiedTsFilter = filterValues.modifiedTs ? filterValues.modifiedTs.toISOString() : undefined;

			const sortField = tableSort.fieldName || undefined;
			const sort = tableSort.fieldName ? tableSort.sort : undefined;

			documentClassService.getExportInterfaceStates(
				props.documentClassId,
				props.exportId,
				filterValues.status || undefined,
				false,
				filterValues.id,
				filterValues.documentId,
				filterValues.interfaceId,
				filterValues.result || undefined,
				modifiedTsFilter,
				sortField,
				sort,
				pagination.page,
				pagination.pageSize
			).then((data) => {
				if (data.elements) {
					exportStates.value = data.elements;
				}
				Object.assign(pagination, data.pagination);
			}).catch((error: {message: string}) => {
				ToastManager.showError(toast, t('Squeeze.General.ErrorDelete'), t('Squeeze.General.DeleteError') + ": " + error.message);
			}).finally(() => {
				loading.value = false;
				getExportInterfaces();
			})
		}

		/** Triggered when page is changed */
		const onChangePage = (event: { rows: number }) => {
			TableSettings.saveTableListPagination(t, toast, store, userApi, route.name, event.rows);
		}

		/** Triggered when a page is changed */
		const onPage = (event: { page: number; rows: number }) => {
			pagination.page = event.page;
			pagination.pageSize = event.rows;
			getExportInterfaceStates();

			onChangePage(event);
		}

		/**
		 * Triggered when a filter has been entered
		 * @param tableFilters
		 */
		const onFilter = (tableFilters: DocumentFilterObject) => {
			pagination.page = 0;
			filterValues.status = tableFilters.status?.value ?? '';
			filterValues.id = tableFilters.id?.value ? Number(tableFilters.id.value) : undefined;
			filterValues.documentId = tableFilters.documentId?.value ? Number(tableFilters.documentId.value) : undefined;
			filterValues.result = tableFilters.result?.value ?? '';
			filterValues.modifiedTs = tableFilters.timeStampDate?.value ?? undefined;

			const interfaceNameFilter: string = tableFilters.interfaceName?.value ?? '';
			if (interfaceNameFilter) {
				const match = allExportInterfaces.value.find(i =>
					i.interfaceName?.toLowerCase().includes(interfaceNameFilter.toLowerCase())
				);
				filterValues.interfaceId = match?.id;
			} else {
				filterValues.interfaceId = undefined;
			}

			getExportInterfaceStates();
		}

		/**
		 * Triggered when a column sort is changed
		 * @param fieldName
		 * @param sortOrder
		 */
		const onSort = (fieldName: string, sortOrder: number) => {
			pagination.page = 0;
			if (fieldName) {
				tableSort.fieldName = fieldName;
				tableSort.sort = sortOrder === -1 ? 'DESC' : 'ASC';
			} else {
				tableSort.fieldName = '';
				tableSort.sort = 'ASC';
			}
			getExportInterfaceStates();
		}

		onMounted(() => {
			pagination.pageSize = TableSettings.getTableListPagination(store, route.name);
			getExportInterfaceStates();
		});

		return {
			t,
			toast,
			loading,
			exportStates,
			pagination,
			allExportInterfaces,
			onPage,
			onFilter,
			onSort,
			getExportInterfaces,
		}
	},
})
</script>

<style scoped>

.child-content {
	height: calc(100vh - 5.6rem - 3.5rem);
}

</style>

